/**
 * Phase 3c polish — dedicated `/nominate` route.
 *
 * The `/employees` "Who's this for?" nominate card used to bounce users
 * through `/apply`'s state machine (start on the lookup surface, then
 * pick the "nominate" option). This direct route skips all of that and
 * lands them on the "Tell us about your workplace" form immediately.
 *
 * Adds the HR contact fields (name optional, email required) per user
 * polish pass — we need a mailbox to reach out to on the employee's
 * behalf when ops works the pending_nominations queue.
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import BackLink from "../components/BackLink";
import { track } from "../lib/analytics";
import { nominateEmployer } from "../lib/api";
import { isValidEmail, titleCaseName } from "../lib/dataHygiene";
import "./Apply.css";

export default function Nominate() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [params] = useSearchParams();

    const [companyName, setCompanyName] = useState("");
    const [companyWebsite, setCompanyWebsite] = useState("");
    const [hrName, setHrName] = useState("");
    const [hrEmail, setHrEmail] = useState("");
    const [role, setRole] = useState("");
    const [email, setEmail] = useState(params.get("email") || "");
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        document.title = t("nominate.title");
        track("nominate_view", { source: "employee_marketing_nominate" });
    }, [t]);

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        if (!companyName.trim()) { setErr(t("nominate.err.company")); return; }
        if (!isValidEmail(email.trim())) { setErr(t("auth.common.emailInvalid")); return; }
        if (!isValidEmail(hrEmail.trim())) { setErr(t("auth.common.emailInvalid")); return; }
        setErr(null);
        setSubmitting(true);
        try {
            await nominateEmployer({
                submitter_email: email.trim(),
                company_name: companyName.trim(),
                target_company_website: companyWebsite.trim() || undefined,
                role: role.trim() || undefined,
                target_hr_name: hrName.trim() || undefined,
                target_hr_email: hrEmail.trim(),
                source: "employee_marketing_nominate",
                locale: i18n.language || "en",
            });
            track("nominate_submit.success", {});
            setSent(true);
        } catch (e2) {
            track("nominate_submit.failure", {});
            setErr(e2?.response?.data?.detail || t("nominate.err.generic"));
        } finally {
            setSubmitting(false);
        }
    };

    if (sent) {
        return (
            <section className="apply-hub-page" data-testid="nominate-sent">
                <div className="apply-hub-wrap">
                    <BackLink
                        to="/employees"
                        testId="nominate-back-sent"
                        label={t("auth.common.startOver")}
                    />
                    <div className="apply-surface">
                        <div className="surface-label">{t("nominate.sentLabel")}</div>
                        <h1 className="surface-title">
                            {t("nominate.sentH1a")} <em>{t("nominate.sentH1b")}</em>
                        </h1>
                        <p className="surface-lede">{t("nominate.sentBody")}</p>
                        <button
                            type="button"
                            className="surface-btn surface-btn-primary"
                            onClick={() => navigate("/employees")}
                            data-testid="nominate-done"
                            style={{ marginTop: "1.5rem" }}
                        >
                            {t("nominate.done")}
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="apply-hub-page" data-testid="nominate-page">
            <div className="apply-hub-wrap">
                <BackLink
                    to="/employees"
                    testId="nominate-back"
                    label={t("auth.common.backToOptions")}
                />
                <div className="apply-surface">
                    <div className="surface-label">{t("nominate.label")}</div>
                    <h1 className="surface-title">
                        {t("nominate.h1a")} <em>{t("nominate.h1b")}</em>
                    </h1>
                    <p className="surface-lede">{t("nominate.lede")}</p>
                    {err && <div className="surface-err">{typeof err === "string" ? err : err?.message}</div>}

                    <form className="surface-form" onSubmit={submit}>
                        <div>
                            <span className="surface-label-inline">{t("nominate.companyLabel")}</span>
                            <input
                                type="text"
                                className="surface-input"
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                                required
                                autoFocus
                                data-testid="nominate-company-input"
                            />
                        </div>
                        <div>
                            <span className="surface-label-inline">{t("nominate.websiteLabel")}</span>
                            <input
                                type="text"
                                className="surface-input"
                                value={companyWebsite}
                                onChange={(e) => setCompanyWebsite(e.target.value)}
                                placeholder={t("nominate.websitePlaceholder")}
                                data-testid="nominate-website-input"
                            />
                        </div>
                        <div>
                            <span className="surface-label-inline">{t("nominate.hrNameLabel")}</span>
                            <input
                                type="text"
                                className="surface-input"
                                value={hrName}
                                onChange={(e) => setHrName(e.target.value)}
                                onBlur={(e) => setHrName(titleCaseName(e.target.value))}
                                placeholder={t("nominate.hrNamePlaceholder")}
                                data-testid="nominate-hr-name-input"
                            />
                        </div>
                        <div>
                            <span className="surface-label-inline">{t("nominate.hrEmailLabel")}</span>
                            <input
                                type="email"
                                className="surface-input"
                                value={hrEmail}
                                onChange={(e) => setHrEmail(e.target.value)}
                                onBlur={(e) => setHrEmail(e.target.value.trim())}
                                placeholder={t("nominate.hrEmailPlaceholder")}
                                required
                                data-testid="nominate-hr-email-input"
                            />
                            <div className="surface-help">{t("nominate.hrEmailHelp")}</div>
                        </div>
                        <div>
                            <span className="surface-label-inline">{t("nominate.roleLabel")}</span>
                            <input
                                type="text"
                                className="surface-input"
                                value={role}
                                onChange={(e) => setRole(e.target.value)}
                                placeholder={t("nominate.rolePlaceholder")}
                                data-testid="nominate-role-input"
                            />
                        </div>
                        <div>
                            <span className="surface-label-inline">{t("nominate.emailLabel")}</span>
                            <input
                                type="email"
                                className="surface-input"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onBlur={(e) => setEmail(e.target.value.trim())}
                                required
                                data-testid="nominate-email-input"
                            />
                        </div>
                        <div className="surface-actions-row">
                            <button
                                type="submit"
                                className="surface-btn surface-btn-primary"
                                disabled={submitting}
                                data-testid="nominate-submit"
                            >
                                {submitting ? t("nominate.submitting") : t("nominate.submit")}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
}
